import { useAuth } from '../../contexts/AuthContext';
import { Navigate, Outlet, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import {
  LayoutDashboard, BookOpen, FileText, Headphones, Settings, LogOut,
  ShoppingCart, Wrench, Clipboard, PhoneCall, Package, CalendarDays,
  Database, Archive, FileSignature, ShoppingBag, UserCheck, Store, ShieldCheck,
} from 'lucide-react';

// Keys must mirror backend NAV_KEYS in routes/permissions.py
const NAV = [
  { key: 'uebersicht', to: '/dashboard', icon: LayoutDashboard, label: 'Übersicht', end: true },
  { key: 'wissensdatenbank', to: '/dashboard/wissensdatenbank', icon: BookOpen, label: 'Wissensdatenbank' },
  { key: 'dokumente', to: '/dashboard/dokumente', icon: FileText, label: 'Dokumente' },
  { key: 'tickets', to: '/dashboard/tickets', icon: Headphones, label: 'Support / Tickets', badgeKey: 'tickets' },
  { key: 'aufgaben', to: '/dashboard/aufgaben', icon: Clipboard, label: 'Aufgaben', badgeKey: 'aufgaben' },
  { key: 'kalender', to: '/dashboard/kalender', icon: CalendarDays, label: 'Kalender' },
  { key: 'systemdatenbank', to: '/dashboard/systemdatenbank', icon: Database, label: 'Systemdatenbank' },
  { key: 'konfigurator', to: '/dashboard/konfigurator', icon: Wrench, label: 'Konfigurator' },
  { key: 'lager', to: '/dashboard/lager', icon: Archive, label: 'Lager' },
  { key: 'angebote', to: '/dashboard/angebote', icon: ShoppingCart, label: 'Anfragen / Angebote', badgeKey: 'angebote' },
  { key: 'bestellungen', to: '/dashboard/bestellungen', icon: ShoppingBag, label: 'Bestellungen', badgeKey: 'bestellungen' },
  { key: 'auftraege', to: '/dashboard/auftraege', icon: Package, label: 'Aufträge', badgeKey: 'auftraege' },
  { key: 'dokumentenarchiv', to: '/dashboard/dokumente-archiv', icon: FileSignature, label: 'Begleitdokumente' },
  { key: 'telefonassistent', to: '/dashboard/telefonassistent', icon: PhoneCall, label: 'Telefonassistent' },
  // B2B-Shop-Kunden-Sicht
  { key: 'shop', to: '/dashboard/shop', icon: Store, label: 'Produktkatalog' },
  { key: 'systempruefung', to: '/dashboard/systempruefung', icon: ShieldCheck, label: 'Systemprüfung' },
  { key: 'meine-bestellungen', to: '/dashboard/meine-bestellungen', icon: ShoppingBag, label: 'Bestellungen' },
  { key: 'einstellungen', to: '/dashboard/einstellungen', icon: Settings, label: 'Einstellungen' },
];

export default function DashboardLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [allowed, setAllowed] = useState(null);
  const [badges, setBadges] = useState({});

  useEffect(() => {
    if (!user) return;
    (async () => {
      const r = await authFetch('/api/permissions/me');
      if (r.ok) { const d = await r.json(); setAllowed(new Set(d.navKeys || [])); }
      else { setAllowed(new Set(NAV.map(n => n.key))); }   // fallback: show all
    })();
  }, [user]);

  // Badge-Counts: alle 60s pollen + sofort beim Login
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await authFetch('/api/badges');
        if (r.ok && !cancelled) setBadges(await r.json());
      } catch {}
    };
    load();
    const intervalId = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [user]);

  if (loading) return <div className="min-h-screen bg-[#f5f6f7] flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#9ec043] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/dashboard/login" state={{ from: location }} replace />;

  // Customer auf der Übersichts-URL → automatisch in den Shop schicken,
  // da Kunden keine Zugriffsrechte auf das interne Sales-Dashboard haben.
  const isCustomer = user.role === 'customer';
  if (isCustomer && (location.pathname === '/dashboard' || location.pathname === '/dashboard/')) {
    return <Navigate to="/dashboard/shop" replace />;
  }

  // Customer-only Nav-Keys — diese tauchen NUR für Kunden auf.
  const CUSTOMER_ONLY = new Set(['shop', 'meine-bestellungen', 'systempruefung']);
  const visible = NAV.filter(n => {
    if (!allowed || !allowed.has(n.key)) return false;
    if (CUSTOMER_ONLY.has(n.key) && !isCustomer) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f5f6f7] flex">
      <aside className="w-60 bg-white border-r border-gray-200 flex flex-col shrink-0">
        <div className="p-4 border-b border-gray-100">
          <img src="/ees-logo.png" alt="EES" className="h-8" />
          <p className="text-[10px] text-gray-400 mt-1">{isCustomer ? 'B2B-Shop' : 'Internes Dashboard'}</p>
        </div>
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {visible.map(n => {
            const badgeCount = n.badgeKey ? (badges[n.badgeKey] || 0) : 0;
            return (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-[#9ec043]/10 text-[#9ec043] font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <span className="relative shrink-0">
                  <n.icon className="w-4 h-4" />
                  {badgeCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white"
                      data-testid={`nav-badge-${n.key}`}
                      title={`${badgeCount} offen`}
                    />
                  )}
                </span>
                <span className="flex-1">{n.label}</span>
                {badgeCount > 0 && (
                  <span
                    className="text-[10px] font-semibold text-red-500 tabular-nums"
                    data-testid={`nav-badge-count-${n.key}`}
                  >
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-gray-100">
          <div className="text-xs text-gray-400 mb-1">{user.name}</div>
          <div className="text-[10px] text-gray-300 capitalize mb-2">{user.role}</div>
          <button onClick={logout} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors" data-testid="btn-logout">
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
